import { KeyValuePair } from "client/Api/common";
import { HttpRequestError } from "client/Api/httpClient";
import {
	GeneralMappingStepConfigurationSaveModel,
	GeneralIntegrationFlowValidationResultModel,
	IntegrationFlowCapabilityKind,
	IntegrationFlowConfigurationValidateResponseModel,
	IntegrationFlowKind,
	IntegrationFlowRequestResultModel,
	MappingStepConfigurationSaveModel,
	MappingStepConfigurationWithGeneralSaveModel,
	MappingStepConfigurationWithPlanningSaveModel,
	PerItemRequestStepConfigurationSaveModel,
	PlanningIntegrationFlowValidationResultModel,
	PlanningMappingStepConfigurationSaveModel,
	ResultMapperColorConditionModel,
	ResultMapperStyledFieldSaveModel,
	RequestMethod,
	RequestStepConfigurationSaveModel,
	StepKind,
	ResultMapperColor
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { TagProps } from "client/Components";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	IntegrationFlowAfasAuthenticationSettings,
	IntegrationFlowAuthenticationKind,
	IntegrationFlowClientCertificateAuthenticationSettings,
	IntegrationFlowOAuth2AuthenticationSettings,
	KeyedValueSaveModel
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";

export const stepStatusToLocalizationKey: Record<StepStatus, LocalizationKey> = {
	["active"]: "_Active",
	["error"]: "_Error",
	["missing-data"]: "_MissingData"
};

export type IntegrationFlowKindCapabilities = {
	optional: readonly IntegrationFlowCapabilityKind[];
	required: readonly IntegrationFlowCapabilityKind[];
};

export const integrationFlowKindToCapabilities: {[key in IntegrationFlowKind]: IntegrationFlowKindCapabilities} = {
	[IntegrationFlowKind.General]: {
		optional: [IntegrationFlowCapabilityKind.Search],
		required: EMPTY_ARRAY
	},
	[IntegrationFlowKind.Planning]: {
		optional: [],
		required: [IntegrationFlowCapabilityKind.DateRange]
	}
};

export type DateRangeFormData = {
	start: Date | null;
	end: Date | null;
};

export type InputDataFormData = {
	capabilities: readonly IntegrationFlowCapabilityKind[];
	searchTerm: string;
	dateRange: DateRangeFormData;
};

export function createEmptyInputData(
	capabilities: readonly IntegrationFlowCapabilityKind[]
): InputDataFormData {
	return {
		capabilities,
		searchTerm: "",
		dateRange: {
			start: null,
			end: null
		}
	};
}

export function requiresInputData(
	capabilities: readonly IntegrationFlowCapabilityKind[]
): boolean {
	return capabilities.some(capabilityRequiresInputData);
}

function capabilityRequiresInputData(kind: IntegrationFlowCapabilityKind): boolean {
	switch (kind) {
		case IntegrationFlowCapabilityKind.Search:
		case IntegrationFlowCapabilityKind.DateRange:
			return true;
		default:
			never(kind);
	}
}

export type RequestStepConfiguration = {
	kind: StepKind.Request;
} & OverrideProperties<
	RequestStepConfigurationSaveModel,
	{
		queryParameters: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
		headers: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
	}
>;

export type PerItemRequestStepConfiguration = {
	kind: StepKind.PerItemRequest;
} & OverrideProperties<
	PerItemRequestStepConfigurationSaveModel,
	{
		maxItems: number | null;
		queryParameters: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
		headers: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
	}
>;

export type MappingStepConfiguration = {
	kind: StepKind.Mapping;
	mapping: MappingStepConfigurationSaveModel | null;
};

export type ResultMapperColorConditionModelFormData = ObjectWithClientId<OverrideProperties<
	ResultMapperColorConditionModel,
	{
		color: ResultMapperColor | null;
	}
>>;

export type ResultMapperStyledFieldFormData = OverrideProperties<
	ResultMapperStyledFieldSaveModel,
	{
		colors: readonly ResultMapperColorConditionModelFormData[];
	}
>
	& {
		shouldIncludeColors: boolean;
	};

export type GeneralMappingStepConfigurationFormData = OverrideProperties<
	GeneralMappingStepConfigurationSaveModel,
	{
		title: ResultMapperStyledFieldFormData;
		date: ResultMapperStyledFieldFormData;
		subtitle1: ResultMapperStyledFieldFormData;
		subtitle2: ResultMapperStyledFieldFormData;
		subtitle3: ResultMapperStyledFieldFormData;
		description: ResultMapperStyledFieldFormData;
	}
>;

export type MappingStepConfigurationWithGeneralFormData = OverrideProperties<
	MappingStepConfigurationWithGeneralSaveModel,
	{
		general: GeneralMappingStepConfigurationFormData;
	}
>;

export type PlanningMappingStepConfigurationFormData = OverrideProperties<
	PlanningMappingStepConfigurationSaveModel,
	{
		title: ResultMapperStyledFieldFormData;
		startDateTime: ResultMapperStyledFieldFormData;
		endDateTime: ResultMapperStyledFieldFormData;
		subtitle1: ResultMapperStyledFieldFormData;
		subtitle2: ResultMapperStyledFieldFormData;
		subtitle3: ResultMapperStyledFieldFormData;
		description: ResultMapperStyledFieldFormData;
		detail1: ResultMapperStyledFieldFormData;
		detail2: ResultMapperStyledFieldFormData;
		detail3: ResultMapperStyledFieldFormData;
		detail4: ResultMapperStyledFieldFormData;
		detail5: ResultMapperStyledFieldFormData;
	}
>;

export type MappingStepConfigurationWithPlanningFormData = OverrideProperties<
	MappingStepConfigurationWithPlanningSaveModel,
	{
		planning: PlanningMappingStepConfigurationFormData;
	}
>;

export type StepConfiguration =
	| RequestStepConfiguration
	| PerItemRequestStepConfiguration
	| MappingStepConfiguration;

export type StepStatus =
	| "active"
	| "error"
	| "missing-data";

export type HttpStatusKind =
	| "successful"
	| "error";

export function isGeneralMappingStep(
	mappingStep: MappingStepConfigurationSaveModel
): mappingStep is MappingStepConfigurationWithGeneralSaveModel {
	return isNotNil(
		(mappingStep as MappingStepConfigurationWithGeneralSaveModel).general
	);
}

export function isPlanningMappingStep(
	mappingStep: MappingStepConfigurationSaveModel
): mappingStep is MappingStepConfigurationWithPlanningSaveModel {
	return isNotNil(
		(mappingStep as MappingStepConfigurationWithPlanningSaveModel).planning
	);
}

export type IntegrationFlowApiKeyAuthenticationFormData = {
	headers: readonly ObjectWithClientId<KeyedValueSaveModel>[];
	queryParameters: readonly ObjectWithClientId<KeyedValueSaveModel>[];
};

export type IntegrationFlowClientCertificateAuthenticationSettingsFormData = OverrideProperties<
	IntegrationFlowClientCertificateAuthenticationSettings,
	{
		certificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
	}
>
	& { initialFileName: string; };

export type HostFormModel = ObjectWithClientId<{
	value: string;
}>;

export type AuthenticationFormData = {
	name: string;
	kind: IntegrationFlowAuthenticationKind;
	apiKey: IntegrationFlowApiKeyAuthenticationFormData;
	oAuth2: IntegrationFlowOAuth2AuthenticationSettings;
	clientCertificate: IntegrationFlowClientCertificateAuthenticationSettingsFormData;
	afas: IntegrationFlowAfasAuthenticationSettings;
	allowedHosts: OptionalFormDataModel<readonly HostFormModel[]>;
};

export function getStatusCodeKind(
	statusCode: number
): HttpStatusKind {
	if (statusCode >= 200 && statusCode <= 299) {
		return "successful";
	}

	if (statusCode >= 400 && statusCode <= 599) {
		return "error";
	}

	console.error(`Status code ${statusCode} is not supported`);

	return "error";
};

// Not localized, this is a technical standard
export const methodToTextMap: Record<RequestMethod, string> = {
	[RequestMethod.Get]: "GET",
	[RequestMethod.Post]: "POST"
};

export function getRequestDescription(step: RequestStepConfiguration | PerItemRequestStepConfiguration): string {
	let {
		method,
		queryParameters,
		url
	} = step;

	if (queryParameters.length > 0) {
		try {
			const builder = new URL(url);
			for (const parameter of queryParameters) {
				builder.searchParams
					.set(parameter.key, parameter.value);
			}
			// searchParams.set will encode the values, so we decode it again
			builder.search = decodeURIComponent(builder.search);
			url = builder.toString();
		} catch (e) { /* Ignore error, show the url field */}
	}

	return methodToTextMap[method] + " | " + url;
}

export type StepTagConfiguration = Pick<TagProps, "text" | "color">;

export function getRequestStepTag(
	hasError: boolean,
	clientId: Guid,
	stepResults: Map<Guid, IntegrationFlowRequestResultModel>
): StepTagConfiguration | undefined {
	return statusToTagConfigurationMap[
		hasError
			? "error"
			: responseToStepStatus(stepResults.get(clientId)) ?? "unknown"
	];
};

export const statusToTagConfigurationMap: Record<StepStatus | "unknown", StepTagConfiguration | undefined> = {
	["unknown"]: undefined,
	["active"]: { text: localize(stepStatusToLocalizationKey.active), color: "success" },
	["error"]: { text: localize(stepStatusToLocalizationKey.error), color: "danger" },
	["missing-data"]: { text: localize(stepStatusToLocalizationKey["missing-data"]), color: "warning" }
};

function responseToStepStatus(
	response: IntegrationFlowRequestResultModel | undefined
): StepStatus | undefined {
	if (!response || !response.statusCode) {
		return;
	}

	const statusCodeKind: HttpStatusKind = getStatusCodeKind(response?.statusCode);

	switch(statusCodeKind) {
		case "error":
			return "error";
		case "successful":
			return "active";
		default:
			never(statusCodeKind);
	}
}

export function isGeneralValidationResults(result: IntegrationFlowConfigurationValidateResponseModel): result is GeneralIntegrationFlowValidationResultModel {
	return isNotNil(
		(result as GeneralIntegrationFlowValidationResultModel).generalSample
	);
};

export function isPlanningValidationResults(
	result: IntegrationFlowConfigurationValidateResponseModel
): result is PlanningIntegrationFlowValidationResultModel {
	return isNotNil(
		(result as PlanningIntegrationFlowValidationResultModel).planningSample
	);
}

export function getResultMapperStepStatus(
	step: ObjectWithClientId<MappingStepConfiguration>,
	validateResponse: IntegrationFlowConfigurationValidateResponseModel | null,
	stepWithErrorIds: ReadonlySet<Guid>
): StepStatus | "unknown" {
	const clientId: Guid = getClientId(step);
	const hasError: boolean = stepWithErrorIds.has(clientId);

	if (hasError) {
		return "error";
	}

	if (isNil(validateResponse) || isNil(step.mapping)) {
		return "unknown";
	}

	if (isGeneralValidationResults(validateResponse)
		&& isGeneralMappingStep(step.mapping)
	) {
		if (!validateResponse.generalSample
			.isSet
		) {
			return "unknown";
		}

		const {
			title,
			link,
			dateTime,
			description,
			subTitle1,
			subTitle2,
			subTitle3
		} = validateResponse
			.generalSample
			.value;

		const {
			title: titleField,
			linkExpression,
			date: dateField,
			description: descriptionField,
			subtitle1: subtitle1Field,
			subtitle2: subtitle2Field,
			subtitle3: subtitle3Field
		} = step
			.mapping
			.general;

		const hasMissingData: boolean = [
			[titleField.expression, title?.value],
			[linkExpression, link],
			[dateField?.expression, dateTime?.value],
			[descriptionField?.expression, description?.value],
			[subtitle1Field?.expression, subTitle1?.value],
			[subtitle2Field?.expression, subTitle2?.value],
			[subtitle3Field?.expression, subTitle3?.value]
		].some(
			([formValue, sampleValue]) => isNotNil(formValue)
				&& Boolean(formValue)
				&& isNil(sampleValue)
		);

		return hasMissingData
			? "missing-data"
			: "active";
	}

	if (isPlanningValidationResults(validateResponse)
		&& isPlanningMappingStep(step.mapping)
	) {
		if (!validateResponse.planningSample
			.isSet
		) {
			return "unknown";
		}

		const {
			title,
			link,
			startDateTime,
			endDateTime,
			subTitle1,
			subTitle2,
			subTitle3,
			description,
			detail1,
			detail2,
			detail3,
			detail4,
			detail5
		} = validateResponse
			.planningSample
			.value;

		const {
			title: titleField,
			linkExpression,
			startDateTime: startDateTimeField,
			endDateTime: endDateTimeField,
			subtitle1: subtitle1Field,
			subtitle2: subtitle2Field,
			subtitle3: subtitle3Field,
			description: descriptionField,
			detail1: detail1Field,
			detail2: detail2Field,
			detail3: detail3Field,
			detail4: detail4Field,
			detail5: detail5Field
		} = step
			.mapping
			.planning;

		const hasMissingData: boolean = [
			[titleField.expression, title?.value],
			[linkExpression, link],
			[startDateTimeField.expression, startDateTime?.value],
			[endDateTimeField.expression, endDateTime?.value],
			[subtitle1Field?.expression, subTitle1?.value],
			[subtitle2Field?.expression, subTitle2?.value],
			[subtitle3Field?.expression, subTitle3?.value],
			[descriptionField?.expression, description?.value],
			[detail1Field?.expression, detail1?.value],
			[detail2Field?.expression, detail2?.value],
			[detail3Field?.expression, detail3?.value],
			[detail4Field?.expression, detail4?.value],
			[detail5Field?.expression, detail5?.value]
		].some(
			([formValue, sampleValue]) => isNotNil(formValue)
				&& Boolean(formValue)
				&& isNil(sampleValue)
		);

		return hasMissingData
			? "missing-data"
			: "active";
	}

	return "unknown";
}

export function tryParseErrorResult<TModel>(
	error: unknown
): TryResult<TModel> {
	if (!(error instanceof HttpRequestError && error.statusCode === 409)) {
		return { wasSuccessful: false };
	}

	try {
		const result: TModel = error.readJson<TModel>();

		return {
			wasSuccessful: true,
			result
		};
	} catch (e) {
		console.error(e);
		return { wasSuccessful: false };
	}
}

export const integrationFlowKindToLocalizationKey: {[key in IntegrationFlowKind]: LocalizationKey} = {
	[IntegrationFlowKind.General]: "_General",
	[IntegrationFlowKind.Planning]: "_PlanningCalendar"
};

export type RequestStepKind = Extract<StepKind,
	| StepKind.Request
	| StepKind.PerItemRequest
>;
